import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';
import {
  Gauge,
  ChevronRight,
  Zap,
  Shield,
  Clock,
  Award,
  ArrowRight,
  Check,
  Search,
  TrendingUp,
} from 'lucide-react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import LanguageSelector from '../components/LanguageSelector';
import { WorkCard, WorkModal } from '../components/ShowcaseCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShowcaseSection = () => {
  const { t } = useTranslation();
  const [works, setWorks] = useState([]);
  useEffect(() => {
    axios.get(`${API}/showcase?featured=true`)
      .then(r => {
        const w = r.data.works || [];
        if (w.length > 0) { setWorks(w.slice(0, 8)); return; }
        return axios.get(`${API}/showcase`).then(r2 => setWorks((r2.data.works || []).slice(0, 8)));
      })
      .catch(() => {});
  }, []);
  const [selected, setSelected] = useState(null);
  if (works.length === 0) return null;
  return (
    <section className="py-20 px-4 relative z-10" data-testid="showcase-section">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10">
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-3">{t('TRABAJOS REALIZADOS')}</h2>
          <p className="text-zinc-400">{t('Vehículos reales reprogramados por nuestro equipo, con fotos y gráficas de potencia')}</p>
        </div>
        <div className="flex gap-6 overflow-x-auto pb-4 snap-x" data-testid="showcase-carousel">
          {works.map(w => (
            <div key={w.id} className="min-w-[300px] max-w-[300px] snap-start">
              <WorkCard work={w} onClick={() => setSelected(w)} />
            </div>
          ))}
        </div>
        <div className="text-center mt-6">
          <Link to="/trabajos" className="text-primary hover:underline font-medium" data-testid="showcase-see-all">
            {t('Ver todos los trabajos')} →
          </Link>
        </div>
      </div>
      <WorkModal work={selected} onClose={() => setSelected(null)} />
    </section>
  );
};

const VehicleSearchWidget = () => {
  const { t } = useTranslation();
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [years, setYears] = useState([]);
  const [motors, setMotors] = useState([]);
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [year, setYear] = useState('');
  const [motor, setMotor] = useState('');
  const [result, setResult] = useState(null);
  const [works, setWorks] = useState([]);
  const [selectedWork, setSelectedWork] = useState(null);

  useEffect(() => { axios.get(`${API}/catalog/brands`).then(r => setBrands(r.data.brands || [])).catch(() => {}); }, []);
  useEffect(() => { axios.get(`${API}/showcase`).then(r => setWorks(r.data.works || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!brand) { setModels([]); setModel(''); setYears([]); setYear(''); setMotors([]); setMotor(''); setResult(null); return; }
    axios.get(`${API}/catalog/models?brand=${encodeURIComponent(brand)}`).then(r => setModels(r.data.models || [])).catch(() => {});
    setModel(''); setYears([]); setYear(''); setMotors([]); setMotor(''); setResult(null);
  }, [brand]);
  useEffect(() => {
    if (!brand || !model) { setYears([]); setYear(''); setMotors([]); setMotor(''); setResult(null); return; }
    axios.get(`${API}/catalog/years?brand=${encodeURIComponent(brand)}&model=${encodeURIComponent(model)}`).then(r => setYears(r.data.years || [])).catch(() => {});
    setYear(''); setMotors([]); setMotor(''); setResult(null);
  }, [brand, model]);
  useEffect(() => {
    if (!brand || !model) { setMotors([]); setMotor(''); return; }
    const p = new URLSearchParams({ brand, model }); if (year) p.append('year', year);
    axios.get(`${API}/catalog/engines?${p}`).then(r => setMotors([...new Set((r.data.engines || []).map(e => e.engine))])).catch(() => {});
    setMotor(''); setResult(null);
  }, [brand, model, year]);

  // Show result when motor selected
  useEffect(() => {
    if (!brand || !model || !motor) { setResult(null); return; }
    const p = new URLSearchParams({ brand, model, q: motor, limit: '1' });
    axios.get(`${API}/catalog/search?${p}`).then(r => {
      const v = (r.data.vehicles || []).find(v => v.engine === motor);
      if (v) setResult(v);
    }).catch(() => {});
  }, [brand, model, year, motor]);

  const s1 = result ? (result.stages || []).find(s => s.stage === 1) : null;

  // Match published showcase works to the searched vehicle:
  // 1) explicit manual association (catalog_brand + catalog_model), or
  // 2) fuzzy brand+model fallback.
  const norm = (v) => (v || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const matchedWorks = result ? works.filter(w => {
    if (w.catalog_brand && w.catalog_model) {
      if (norm(w.catalog_brand) !== norm(result.brand)) return false;
      if (norm(w.catalog_model) !== norm(result.model)) return false;
      if (w.catalog_engine && norm(w.catalog_engine) !== norm(result.engine)) return false;
      return true;
    }
    if (norm(w.brand) !== norm(result.brand)) return false;
    const wm = norm(w.model), rm = norm(result.model);
    return wm && rm && (wm.includes(rm) || rm.includes(wm));
  }) : [];

  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <select value={brand} onChange={e => setBrand(e.target.value)}
          className="bg-surface-highlight border border-white/10 rounded px-3 py-3 text-white text-sm" data-testid="landing-brand">
          <option value="">{t('Marca')}</option>
          {brands.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <select value={model} onChange={e => setModel(e.target.value)} disabled={!brand}
          className="bg-surface-highlight border border-white/10 rounded px-3 py-3 text-white text-sm disabled:opacity-40" data-testid="landing-model">
          <option value="">{t('Modelo')}</option>
          {models.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
        <select value={year} onChange={e => setYear(e.target.value)} disabled={!model}
          className="bg-surface-highlight border border-white/10 rounded px-3 py-3 text-white text-sm disabled:opacity-40">
          <option value="">{t('Año')}</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={motor} onChange={e => setMotor(e.target.value)} disabled={!model}
          className="bg-surface-highlight border border-white/10 rounded px-3 py-3 text-white text-sm disabled:opacity-40">
          <option value="">{t('Motor')}</option>
          {motors.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      {/* Result */}
      {result && s1 && (
        <div className="bg-black/40 border border-primary/20 rounded-lg p-4 mt-4 text-center animate-in fade-in" data-testid="landing-result">
          <p className="text-zinc-400 text-sm mb-3">{result.brand} {result.model} {result.engine}</p>
          <div className="flex items-center justify-center gap-6">
            <div>
              <p className="text-zinc-500 text-xs uppercase">Original</p>
              <p className="text-white font-heading text-2xl font-bold">{s1.potencia_original}<span className="text-zinc-500 text-sm">CV</span></p>
              <p className="text-zinc-500 text-sm">{s1.par_original}Nm</p>
            </div>
            <div className="text-primary">
              <ArrowRight className="w-8 h-8" />
            </div>
            <div>
              <p className="text-primary text-xs uppercase font-bold">Stage 1</p>
              <p className="text-primary font-heading text-2xl font-bold">{s1.potencia_repro}<span className="text-primary/60 text-sm">CV</span></p>
              <p className="text-primary/80 text-sm">{s1.par_repro}Nm</p>
            </div>
            <div className="bg-green-500/10 border border-green-500/20 rounded-lg px-4 py-2">
              <p className="text-green-400 text-xs uppercase">{t('Ganancia')}</p>
              <p className="text-green-400 font-heading text-xl font-bold">+{s1.potencia_ganancia}CV</p>
              <p className="text-green-400/70 text-sm">+{s1.par_ganancia}Nm</p>
            </div>
          </div>
          <Link to="/register" className="inline-block mt-4">
            <Button className="bg-primary hover:bg-primary/90 text-white">
              {t('Solicitar Reprogramación')} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      )}

      {/* Matching showcase works for the searched vehicle */}
      {result && matchedWorks.length > 0 && (
        <div className="mt-6" data-testid="landing-matched-works">
          <p className="text-center text-zinc-400 text-sm mb-4">
            <Award className="w-4 h-4 text-primary inline mr-1.5 -mt-0.5" />
            {t('Ya hemos reprogramado este modelo:')}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            {matchedWorks.slice(0, 4).map(w => (
              <WorkCard key={w.id} work={w} onClick={() => setSelectedWork(w)} />
            ))}
          </div>
        </div>
      )}
      <WorkModal work={selectedWork} onClose={() => setSelectedWork(null)} />
    </div>
  );
};

const LandingPage = () => {
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const services = [
    { name: 'Stage 1', credits: 100, desc: t('Optimización estándar de potencia') },
    { name: 'Stage 1 + Soluciones', credits: 100, desc: t('Stage 1 + DPF/EGR/AdBlue') },
    { name: t('Solución DPF'), credits: 60, desc: t('Gestión filtro partículas') },
    { name: t('Solución EGR'), credits: 60, desc: t('Gestión válvula EGR') },
    { name: t('Solución AdBlue'), credits: 60, desc: t('Gestión sistema AdBlue') },
    { name: 'DTC Removal', credits: 60, desc: t('Gestión códigos error') },
  ];

  const features = [
    { icon: Zap, title: t('Respuesta Rápida'), desc: t('Archivos listos en menos de 1 hora') },
    { icon: Shield, title: t('Garantía Total'), desc: t('Archivos probados y verificados') },
    { icon: Clock, title: t('24/7 Disponible'), desc: t('Servicio disponible todo el día') },
    { icon: Award, title: t('Profesionales'), desc: t('Más de 10 años de experiencia') },
  ];

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <div className="noise-overlay" />
      
      {/* Hero Background */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1637300417161-bff1e04ea9f6?crop=entropy&cs=srgb&fm=jpg&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/80 to-background" />
      </div>

      {/* Navigation */}
      <nav className="relative z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <img src="/logo-ar.png" alt="AR Performance" className="w-10 h-10 object-contain" />
              <div>
                <h1 className="font-heading text-xl font-bold text-white tracking-tight">
                  AR PERFORMANCE
                </h1>
                <p className="text-xs text-zinc-500 uppercase tracking-wider">FileService</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <LanguageSelector />
              <Link to="/login">
                <Button variant="ghost" className="text-zinc-300 hover:text-white" data-testid="login-nav-btn">
                  {t('Iniciar Sesión')}
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-primary hover:bg-primary/90 text-white" data-testid="register-nav-btn">
                  {t('Registrarse')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 pt-20 pb-32 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <img src="/logo-ar-text.png" alt="AR Performance" className="h-24 sm:h-32 mx-auto mb-6 object-contain" />
          <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black text-white tracking-tighter mb-6">
            {t('REPROGRAMACIÓN')}
            <br />
            <span className="text-primary">{t('PROFESIONAL')}</span>
          </h1>
          <p className="text-lg sm:text-xl text-zinc-400 max-w-2xl mx-auto mb-8">
            {t('Servicio de archivos de chiptuning para profesionales. Stage 1, Stage 2, DPF, EGR, AdBlue y más soluciones.')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button 
                size="lg" 
                className="bg-primary hover:bg-primary/90 text-white font-heading uppercase tracking-wider px-8 glow-red"
                data-testid="hero-register-btn"
              >
                {t('Comenzar Ahora')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white/20 text-white hover:bg-white/10"
              onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
              data-testid="view-services-btn"
            >
              {t('Ver Servicios')}
            </Button>
          </div>
          
          {/* Stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {[
              { value: '10K+', label: t('Archivos Procesados') },
              { value: '500+', label: t('Clientes Activos') },
              { value: '<1h', label: t('Tiempo Respuesta') },
              { value: '99%', label: t('Satisfacción') },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <p className="font-heading text-3xl sm:text-4xl font-bold text-primary">{stat.value}</p>
                <p className="text-sm text-zinc-500 uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vehicle Search - "Busca tu reprogramación" */}
      <section className="relative z-10 py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-surface/80 backdrop-blur-md border border-white/10 rounded-lg p-6 sm:p-8">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-primary mb-2 text-center">{t('BUSCA TU REPROGRAMACIÓN')}</h2>
            <p className="text-zinc-400 text-sm text-center mb-6">{t('Selecciona tu vehículo y descubre la ganancia de potencia disponible')}</p>
            <VehicleSearchWidget />
          </div>
        </div>
      </section>

      {/* Showcase - Trabajos Realizados (justo debajo del buscador) */}
      <ShowcaseSection />

      {/* Features */}
      <section className="relative z-10 py-20 bg-surface/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <div 
                key={i}
                className="bg-surface border border-white/10 rounded-md p-6 card-hover"
              >
                <feature.icon className="w-10 h-10 text-primary mb-4" />
                <h3 className="font-heading text-xl font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-zinc-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="relative z-10 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">
              {t('NUESTROS SERVICIOS')}
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              {t('1 Crédito = 1€ + IVA. Se cobra el servicio más caro por vehículo.')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => (
              <div 
                key={i}
                className="bg-surface border border-white/10 rounded-md p-6 card-hover group"
                data-testid={`service-card-${i}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <h3 className="font-heading text-2xl font-bold text-white group-hover:text-primary transition-colors">
                    {service.name}
                  </h3>
                  <div className="bg-primary/10 text-primary px-3 py-1 rounded-md">
                    <span className="font-mono font-bold">{service.credits}</span>
                    <span className="text-xs ml-1">{t('créditos')}</span>
                  </div>
                </div>
                <p className="text-zinc-400">{service.desc}</p>
                <div className="mt-4 flex items-center text-zinc-500">
                  <Check className="w-4 h-4 mr-2 text-green-500" />
                  <span className="text-sm">{t('Garantía incluida')}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Showcase - Trabajos Realizados */}

      {/* CTA */}
      <section className="relative z-10 py-20 bg-gradient-to-b from-transparent via-primary/5 to-transparent">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">
            {t('¿LISTO PARA EMPEZAR?')}
          </h2>
          <p className="text-zinc-400 mb-8 text-lg">
            {t('Regístrate ahora y recibe tu primer archivo en menos de 1 hora.')}
          </p>
          <Link to="/register">
            <Button 
              size="lg" 
              className="bg-primary hover:bg-primary/90 text-white font-heading uppercase tracking-wider px-12 glow-red"
              data-testid="cta-register-btn"
            >
              {t('Crear Cuenta Gratis')}
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src="/logo-ar.png" alt="AR Performance" className="w-6 h-6 object-contain" />
              <span className="font-heading text-lg font-bold text-white">AR PERFORMANCE FileService</span>
            </div>
            <p className="text-zinc-500 text-sm">
              © 2026 AR Performance. {t('Todos los derechos reservados.')}
            </p>
            <div className="flex items-center gap-3 text-zinc-600 text-xs mt-2">
              <Link to="/legal/aviso-legal" className="hover:text-zinc-400">{t('Aviso Legal')}</Link>
              <span>·</span>
              <Link to="/legal/privacidad" className="hover:text-zinc-400">{t('Privacidad')}</Link>
              <span>·</span>
              <Link to="/legal/cookies" className="hover:text-zinc-400">Cookies</Link>
              <span>·</span>
              <Link to="/legal/condiciones" className="hover:text-zinc-400">CGC</Link>
              <span>·</span>
              <Link to="/legal/responsabilidad" className="hover:text-zinc-400">Responsabilidad</Link>
            </div>
          </div>
        </div>
      </footer>

      <Toaster position="top-right" richColors />
    </div>
  );
};

export default LandingPage;
